import { useState, useRef } from 'react';

export default function Footer() {
  const [logoUrl, setLogoUrl] = useState<string | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleLogoUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target?.files?.[0];
    if (file) {
      setIsUploading(true);
      const reader = new FileReader();
      reader.onload = (e) => {
        const result = e.target?.result;
        if (result) {
          setLogoUrl(result as string);
        }
        setIsUploading(false);
      };
      reader.onerror = () => {
        setIsUploading(false);
      };
      reader.readAsDataURL(file);
    }
  };

  const triggerFileInput = () => {
    fileInputRef.current?.click();
  };

  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <footer className="bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 text-white py-16 relative overflow-hidden">
      {/* Enhanced gradient overlays for depth and richness */}
      <div className="absolute inset-0 bg-gradient-to-br from-yellow-600/8 via-amber-700/3 to-yellow-800/6"></div>
      <div className="absolute inset-0 bg-gradient-to-tr from-transparent via-yellow-500/2 to-amber-600/4"></div>
      <div className="absolute top-0 right-0 w-96 h-96 bg-gradient-to-bl from-yellow-500/5 via-amber-600/3 to-transparent rounded-full blur-3xl"></div>
      <div className="absolute bottom-0 left-0 w-96 h-96 bg-gradient-to-tr from-amber-700/4 via-yellow-600/2 to-transparent rounded-full blur-3xl"></div>
      
      <div className="max-w-7xl mx-auto px-6 relative">
        <div className="grid md:grid-cols-4 gap-8 mb-12">
          {/* Company Info */}
          <div className="md:col-span-2">
            <div className="flex items-center space-x-3 mb-6">
              <div className="w-12 h-12 bg-gradient-to-br from-yellow-500 via-yellow-600 to-amber-700 rounded-xl flex items-center justify-center shadow-2xl border border-yellow-400/20">
                <i className="ri-building-line text-white text-xl drop-shadow-lg"></i>
              </div>
              
              <div className="ml-4">
                <h3 className="text-2xl font-bold">
                  <span className="text-transparent bg-clip-text bg-gradient-to-r from-yellow-400 via-yellow-300 to-amber-400 drop-shadow-lg font-black tracking-wide">
                    CASTLE
                  </span>
                  <span className="text-white/90 ml-1 drop-shadow-lg font-light">
                    &amp; CASTLE
                  </span>
                </h3>
                <p className="text-transparent bg-clip-text bg-gradient-to-r from-yellow-400 to-amber-500 text-sm font-bold uppercase tracking-widest drop-shadow-sm">
                  MARKETING AGENCY
                </p>
              </div>
            </div>
            <p className="text-stone-100 leading-relaxed mb-6 max-w-md text-lg">
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-yellow-300 via-yellow-400 to-amber-500 font-bold">Full-stack growth marketing agency</span> specializing in data-driven growth operations for fast-growing companies. Your 
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-yellow-400 via-amber-400 to-yellow-500 font-semibold"> prestigious growth team</span> on demand.
            </p>
            <div className="flex space-x-4">
              <a 
                href="https://x.com/CastleCastleHQ" 
                target="_blank" 
                rel="noopener noreferrer" 
                className="w-12 h-12 bg-gradient-to-br from-gray-800/80 via-gray-700/90 to-gray-800/80 rounded-xl flex items-center justify-center hover:bg-gradient-to-br hover:from-yellow-500 hover:via-yellow-600 hover:to-amber-700 transition-all duration-500 cursor-pointer shadow-xl hover:shadow-2xl border border-gray-600/30 hover:border-yellow-400/50 backdrop-blur-sm"
              >
                <i className="ri-twitter-x-line text-white text-lg drop-shadow-lg"></i>
              </a>
              <a 
                href="mailto:castle.castleconsultants@gmail.com" 
                className="w-12 h-12 bg-gradient-to-br from-gray-800/80 via-gray-700/90 to-gray-800/80 rounded-xl flex items-center justify-center hover:bg-gradient-to-br hover:from-yellow-500 hover:via-yellow-600 hover:to-amber-700 transition-all duration-500 cursor-pointer shadow-xl hover:shadow-2xl border border-gray-600/30 hover:border-yellow-400/50 backdrop-blur-sm"
              >
                <i className="ri-mail-line text-white text-lg drop-shadow-lg"></i>
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="text-xl font-bold mb-6">
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-yellow-300 via-yellow-400 to-amber-500 drop-shadow-sm">Quick</span>
              <span className="text-white ml-1 drop-shadow-sm">Links</span>
            </h4>
            <ul className="space-y-4">
              <li>
                <button 
                  onClick={() => scrollToSection('about')}
                  className="text-stone-100 hover:text-transparent hover:bg-clip-text hover:bg-gradient-to-r hover:from-yellow-300 hover:via-yellow-400 hover:to-amber-500 transition-all duration-400 cursor-pointer font-semibold text-base hover:drop-shadow-sm"
                >
                  About Us
                </button>
              </li>
              <li>
                <button 
                  onClick={() => scrollToSection('services')}
                  className="text-stone-100 hover:text-transparent hover:bg-clip-text hover:bg-gradient-to-r hover:from-yellow-300 hover:via-yellow-400 hover:to-amber-500 transition-all duration-400 cursor-pointer font-semibold text-base hover:drop-shadow-sm"
                >
                  Services
                </button>
              </li>
              <li>
                <button 
                  onClick={() => scrollToSection('publicity')}
                  className="text-stone-100 hover:text-transparent hover:bg-clip-text hover:bg-gradient-to-r hover:from-yellow-300 hover:via-yellow-400 hover:to-amber-500 transition-all duration-400 cursor-pointer font-semibold text-base hover:drop-shadow-sm"
                >
                  Publicity
                </button>
              </li>
              <li>
                <button 
                  onClick={() => scrollToSection('showcase')}
                  className="text-stone-100 hover:text-transparent hover:bg-clip-text hover:bg-gradient-to-r hover:from-yellow-300 hover:via-yellow-400 hover:to-amber-500 transition-all duration-400 cursor-pointer font-semibold text-base hover:drop-shadow-sm"
                >
                  Showcase
                </button>
              </li>
              <li>
                <button 
                  onClick={() => scrollToSection('clients')}
                  className="text-stone-100 hover:text-transparent hover:bg-clip-text hover:bg-gradient-to-r hover:from-yellow-300 hover:via-yellow-400 hover:to-amber-500 transition-all duration-400 cursor-pointer font-semibold text-base hover:drop-shadow-sm"
                >
                  Clients
                </button>
              </li>
              <li>
                <button 
                  onClick={() => scrollToSection('testimonials')}
                  className="text-stone-100 hover:text-transparent hover:bg-clip-text hover:bg-gradient-to-r hover:from-yellow-300 hover:via-yellow-400 hover:to-amber-500 transition-all duration-400 cursor-pointer font-semibold text-base hover:drop-shadow-sm"
                >
                  Testimonials
                </button>
              </li>
            </ul>
          </div>

          {/* Request a Quote */}
          <div>
            <h4 className="text-xl font-bold mb-6">
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-yellow-300 via-yellow-400 to-amber-500 drop-shadow-sm">Request a</span>
              <span className="text-white ml-1 drop-shadow-sm">Quote</span>
            </h4>
            <div className="bg-gradient-to-br from-yellow-500/10 via-amber-600/5 to-yellow-700/10 rounded-xl p-6 border border-yellow-400/20 backdrop-blur-sm">
              <p className="text-stone-100 mb-4 leading-relaxed">
                Ready to transform your business with our 
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-yellow-300 to-amber-400 font-semibold"> premium marketing solutions</span>?
              </p>
              <button 
                onClick={() => scrollToSection('contact')}
                className="w-full bg-gradient-to-r from-yellow-500 via-yellow-600 to-amber-700 hover:from-yellow-600 hover:via-amber-600 hover:to-amber-800 text-white font-bold py-3 px-6 rounded-lg transition-all duration-300 shadow-lg hover:shadow-xl cursor-pointer whitespace-nowrap"
              >
                <i className="ri-calendar-line mr-2"></i>
                Get Free Quote
              </button>
            </div>
          </div>
        </div>

        {/* Bottom Section */}
        <div className="border-t border-gradient-to-r from-transparent via-yellow-600/30 to-transparent pt-8">
          <div className="w-full h-px bg-gradient-to-r from-transparent via-yellow-500/40 to-transparent mb-8"></div>
          <div className="flex flex-col md:flex-row justify-between items-center">
            <p className="text-stone-200 text-base mb-4 md:mb-0 font-medium">
              © 2024 
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-yellow-300 via-yellow-400 to-amber-500 font-bold mx-2 drop-shadow-sm">
                Castle & Castle Marketing Agency
              </span>
              . All rights reserved.
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
}
